import { createContext, useContext } from 'react';
import type { LoadJobStatus } from './api';
import { valueUpdatesSentence } from './valueUpdates';

/** A load the server accepted and is running in the background, tracked until
 *  it reaches a terminal state. */
export interface ActiveLoadJob {
  id: string;
  filename: string;
  graph: string;
  startedAt: number;
}

/** A finished load, shown once as a dismissible completion notice. On a
 *  success `detail` is what the load left out, in the server's words. */
export interface LoadCompletion {
  id: string;
  filename: string;
  graph: string;
  ok: boolean;
  statements?: number;
  error?: string;
  detail?: string;
  /** What the load did to properties that hold one value, in plain words. */
  updates?: string;
  finishedAt: number;
}

/**
 * Map a polled job status to a completion record, or null while it is still
 * running. A terminal `done` is a success (carrying the written-statement count
 * when the backend reports one); any other terminal state is a failure. Pure,
 * so the terminal-state handling is unit-tested without a running poll loop.
 */
export function completionFromStatus(
  job: LoadJobStatus,
  meta: ActiveLoadJob,
): LoadCompletion | null {
  if (job.state === 'running') return null;
  const base = {
    id: meta.id,
    filename: meta.filename,
    graph: meta.graph,
    finishedAt: Date.now(),
  };
  if (job.state === 'done') {
    const updates = valueUpdatesSentence(job.result?.updated, job.result?.not_applied_older);
    return {
      ...base,
      ok: true,
      ...(job.result?.statements !== undefined ? { statements: job.result.statements } : {}),
      ...(job.result?.fuseki_response ? { detail: job.result.fuseki_response } : {}),
      ...(updates ? { updates } : {}),
    };
  }
  return {
    ...base,
    ok: false,
    error: job.error ?? 'load failed',
    ...(job.detail !== undefined ? { detail: job.detail } : {}),
  };
}

export interface LoadJobsContextValue {
  active: ActiveLoadJob[];
  completions: LoadCompletion[];
  addLoadJob: (id: string, meta: Omit<ActiveLoadJob, 'id'>) => void;
  dismissCompletion: (id: string) => void;
}

export const LoadJobsContext = createContext<LoadJobsContextValue | null>(null);

export function useLoadJobs(): LoadJobsContextValue {
  const ctx = useContext(LoadJobsContext);
  if (!ctx) throw new Error('useLoadJobs must be used within a LoadJobsProvider');
  return ctx;
}
